import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, CreditCard, CheckCircle, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { formatCurrency } from '@/lib/utils'
import { useQuery } from 'react-query'
import { qrApi } from '@/services/api'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

interface QRPaymentModalProps {
  qrCodeData: any
  onClose: () => void
  onPaymentSuccess: () => void
}

export function QRPaymentModal({ qrCodeData, onClose, onPaymentSuccess }: QRPaymentModalProps) {
  const { t } = useTranslation()
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [step, setStep] = useState<'amount' | 'confirm' | 'processing' | 'success' | 'error'>('amount')

  const quickAmounts = [10, 25, 50, 100, 200, 500]

  const handleAmountSelect = (selectedAmount: number) => {
    setAmount(selectedAmount.toString())
  }

  const handleNext = () => {
    if (step === 'amount') {
      if (!amount || parseFloat(amount) <= 0) {
        toast.error(t('errors.validationError'))
        return
      }
      setStep('confirm')
    } else if (step === 'confirm') {
      setStep('processing')
      processPayment()
    }
  }

  const processPayment = async () => {
    setIsProcessing(true)
    
    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      const response = await qrApi.processQRPayment({
        qr_code_id: qrCodeData.id,
        amount: parseFloat(amount),
        description: description || t('modals.payment')
      })
      
      setStep('success')
      
      // Auto close after success
      setTimeout(() => {
        onPaymentSuccess()
      }, 2000)
      
    } catch (error: any) {
      console.error('Payment failed:', error)
      setStep('error')
      toast.error(t('modals.paymentError'))
    } finally {
      setIsProcessing(false)
    }
  }

  const handleRetry = () => {
    setStep('amount')
    setAmount('')
    setDescription('')
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 pb-20 md:pb-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-white rounded-2xl w-full max-w-md max-h-[calc(90vh-5rem)] md:max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <h2 className="text-xl font-bold text-text-primary">
              {t('qrScanner.title')}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-text-secondary" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Merchant Info */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <CreditCard className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary">
                {qrCodeData.display_name}
              </h3>
              <p className="text-text-secondary text-sm">
                {qrCodeData.description}
              </p>
            </div>

            {/* Step 1: Amount Input */}
            {step === 'amount' && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    {t('modals.paymentAmount')}
                  </label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={amount}
                    onChange={setAmount}
                    className="text-2xl text-center font-bold"
                  />
                </div>

                {/* Quick Amount Buttons */}
                <div>
                  <p className="text-sm text-text-secondary mb-3">{t('modals.quickAmounts')}:</p>
                  <div className="grid grid-cols-3 gap-2">
                    {quickAmounts.map((quickAmount) => (
                      <Button
                        key={quickAmount}
                        variant="outline"
                        size="sm"
                        onClick={() => handleAmountSelect(quickAmount)}
                        className="text-sm"
                      >
                        {formatCurrency(quickAmount, 'STN')}
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    {t('modals.paymentDescription')} ({t('common.optional')})
                  </label>
                  <Input
                    type="text"
                    placeholder="Ex: Almoço no restaurante"
                    value={description}
                    onChange={setDescription}
                  />
                </div>

                <Button
                  onClick={handleNext}
                  className="w-full"
                  disabled={!amount || parseFloat(amount) <= 0}
                >
                  {t('common.continue')}
                </Button>
              </motion.div>
            )}

            {/* Step 2: Confirmation */}
            {step === 'confirm' && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    Confirmar Pagamento
                  </h3>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-3xl font-bold text-primary">
                      {formatCurrency(parseFloat(amount), 'STN')}
                    </p>
                    <p className="text-text-secondary text-sm mt-1">
                      Para {qrCodeData.display_name}
                    </p>
                    {description && (
                      <p className="text-text-secondary text-sm mt-2">
                        "{description}"
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={handleNext}
                    className="w-full"
                    loading={isProcessing}
                  >
                    Confirmar Pagamento
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setStep('amount')}
                    className="w-full"
                  >
                    Voltar
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Processing */}
            {step === 'processing' && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center space-y-6"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <CreditCard className="h-8 w-8 text-primary" />
                  </motion.div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    Processando Pagamento
                  </h3>
                  <p className="text-text-secondary">
                    Aguarde enquanto processamos sua transação...
                  </p>
                </div>
              </motion.div>
            )}

            {/* Step 4: Success */}
            {step === 'success' && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center space-y-6"
              >
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="h-8 w-8 text-success" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    Pagamento Realizado!
                  </h3>
                  <p className="text-text-secondary">
                    Sua transação foi processada com sucesso.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Step 5: Error */}
            {step === 'error' && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center space-y-6"
              >
                <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto">
                  <AlertCircle className="h-8 w-8 text-error" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    Erro no Pagamento
                  </h3>
                  <p className="text-text-secondary">
                    Não foi possível processar sua transação. Tente novamente.
                  </p>
                </div>
                <div className="space-y-3">
                  <Button
                    onClick={handleRetry}
                    className="w-full"
                  >
                    Tentar Novamente
                  </Button>
                  <Button
                    variant="outline"
                    onClick={onClose}
                    className="w-full"
                  >
                    Cancelar
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}




